/**
 * PostHog adapter for frontend React applications
 * Implements FeatureFlagAdapter interface using posthog-js
 * Reuses existing PostHog instance from usePostHog() hook
 */

import {
	FeatureFlagAdapter,
	FeatureFlagProvider,
	FeatureFlagVariant,
	FeatureFlagConfigWithContext
} from '../types'
import { EvaluationContext } from 'api-models'
import { PostHog } from 'posthog-js'

export class PostHogAdapter implements FeatureFlagAdapter {
	createProvider(config: FeatureFlagConfigWithContext): FeatureFlagProvider {
		return new PostHogProviderWrapper(config)
	}
}

class PostHogProviderWrapper implements FeatureFlagProvider {
	private posthog: PostHog | null = null
	private config: FeatureFlagConfigWithContext

	constructor(config: FeatureFlagConfigWithContext) {
		this.config = config
		// PostHog instance will be set via initialize method
	}

	async initialize(config: FeatureFlagConfigWithContext): Promise<void> {
		this.config = config
		// PostHog instance should be available from the PostHogProvider
		// We'll need to get it from the hook or context
		// For now, we'll set it when the provider is ready
	}

	/**
	 * Set the PostHog instance (called from provider component)
	 */
	setPostHogInstance(posthog: PostHog | null): void {
		this.posthog = posthog
	}

	isEnabled(flagName: string): boolean {
		if (!this.posthog) {
			return false
		}
		return this.posthog.isFeatureEnabled(flagName) || false
	}

	getVariant(flagName: string): FeatureFlagVariant {
		if (!this.posthog) {
			return { enabled: false, name: 'control', payload: undefined }
		}

		const variant = this.posthog.getFeatureFlag(flagName)

		if (variant === false || variant === undefined) {
			return { enabled: false, name: 'control', payload: undefined }
		}

		// Get payload separately using getFeatureFlagPayload
		let payload: Record<string, unknown> | string | number | boolean | null | undefined = undefined
		try {
			const flagPayload = this.posthog.getFeatureFlagPayload(flagName)
			if (flagPayload !== undefined && flagPayload !== null) {
				payload = flagPayload as Record<string, unknown> | string | number | boolean
			}
		} catch (payloadError) {
			// If payload fetch fails, continue without payload
			console.debug(`Could not fetch payload for flag ${flagName}:`, payloadError)
		}

		if (typeof variant === 'string') {
			return { enabled: true, name: variant, payload: payload }
		}

		if (typeof variant === 'object' && variant !== null) {
			return {
				enabled: true,
				name: 'enabled',
				payload: payload || variant
			}
		}

		return { enabled: true, name: 'enabled', payload: payload }
	}

	updateContext(context: Partial<EvaluationContext>): void {
		if (!this.posthog) {
			return
		}

		// Update PostHog person properties
		const personProperties = this.mapContextToPersonProperties(context)
		this.posthog.identify(context.userId || 'anonymous', personProperties)
	}

	on(event: string, callback: (...args: any[]) => void): void {
		if (!this.posthog) {
			return
		}
		// PostHog doesn't have a direct event system for feature flags
		// We can use the onFeatureFlags callback
		if (event === 'ready' || event === 'update') {
			this.posthog.onFeatureFlags(() => {
				callback()
			})
		}
	}

	off(): void {
		// PostHog doesn't have an off method for feature flags
		// This is a no-op
	}

	/**
	 * Map EvaluationContext to PostHog person properties
	 */
	private mapContextToPersonProperties(
		context: Partial<EvaluationContext>
	): Record<string, any> {
		const properties: Record<string, any> = {}

		if (context.email) {
			properties.email = context.email
		}

		if (context.accountId) {
			properties.accountId = context.accountId
		}

		if (context.attributes) {
			Object.assign(properties, context.attributes)
		}

		return properties
	}
}

